import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AuthLayout } from '@/layouts/AuthLayout';
import { apiClient } from '@/api/client';
import { isApiError } from '@/api/errors';

/**
 * Email-verification deep link: identity-service mails `/verify?token=…`
 * (24h, single-use; a new link invalidates older ones).
 *
 * The token is a credential. It is captured once, kept only in this tab's
 * sessionStorage (so a refresh or a retry after a network failure still works),
 * immediately removed from the address bar and history, never rendered or
 * logged, and deleted as soon as the server gives a definitive answer.
 *
 * The API deliberately answers invalid, expired and already-used tokens with
 * the same 400 TOKEN_INVALID_OR_EXPIRED. "Already used" is therefore only shown
 * when THIS browser had already submitted the token (refresh mid-request,
 * retry, or re-opening the same link).
 */

export type VerifyView = 'loading' | 'verified' | 'used' | 'invalid' | 'temporary' | 'missing';
type Outcome = 'verified' | 'used' | 'invalid';

interface VerifyRecord {
  token?: string;
  /** SHA-256 of the token: recognises a re-opened link after the token itself is gone. */
  fp?: string;
  attempted?: boolean;
  outcome?: Outcome;
  at: number;
}

export const VERIFY_STORE_KEY = 'nh.emailVerify';
const OUTCOME_TTL_MS = 60 * 60_000;
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{20,200}$/;

let memoryRecord: VerifyRecord | null = null; // fallback when sessionStorage is unavailable
let pendingCapture: Promise<void> | null = null;
const inFlight = new Map<string, Promise<{ view: VerifyView; rateLimited?: boolean }>>();

function load(): VerifyRecord | null {
  try {
    const raw = sessionStorage.getItem(VERIFY_STORE_KEY);
    return raw ? (JSON.parse(raw) as VerifyRecord) : null;
  } catch {
    return memoryRecord;
  }
}

function save(record: VerifyRecord | null): void {
  memoryRecord = record;
  try {
    if (record) sessionStorage.setItem(VERIFY_STORE_KEY, JSON.stringify(record));
    else sessionStorage.removeItem(VERIFY_STORE_KEY);
  } catch {
    /* memory fallback only */
  }
}

async function fingerprint(token: string): Promise<string | undefined> {
  try {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

async function capture(token: string): Promise<void> {
  if (!TOKEN_SHAPE.test(token)) {
    save({ outcome: 'invalid', at: Date.now() });
    return;
  }
  const fp = await fingerprint(token);
  const prior = load();
  // Same link again (refresh before capture finished, or re-opened after an answer): keep its state.
  if (prior && ((fp && prior.fp === fp) || prior.token === token)) return;
  save({ token, fp, attempted: false, at: Date.now() });
}

function submit(token: string): Promise<{ view: VerifyView; rateLimited?: boolean }> {
  const existing = inFlight.get(token);
  if (existing) return existing;
  const run = (async () => {
    const before = load();
    const resubmission = !!before?.attempted;
    save({ ...before, token, attempted: true, at: Date.now() });
    try {
      await apiClient.post('/users/verify', { token });
      save({ outcome: 'verified', fp: before?.fp, at: Date.now() });
      return { view: 'verified' as const };
    } catch (err) {
      const status = isApiError(err) ? err.status : 0;
      if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
        const outcome: Outcome = resubmission ? 'used' : 'invalid';
        save({ outcome, fp: before?.fp, at: Date.now() });
        return { view: outcome };
      }
      // Network failure, timeout, 429 or 5xx: keep the token so Retry/refresh can finish.
      return { view: 'temporary' as const, rateLimited: status === 429 };
    } finally {
      inFlight.delete(token);
    }
  })();
  inFlight.set(token, run);
  return run;
}

const linkButtonClass =
  'block h-12 w-full rounded-lg bg-brand px-4 text-center font-semibold leading-[3rem] text-brand-fg shadow-sm transition hover:brightness-110';
const buttonClass =
  'h-12 w-full rounded-lg bg-brand px-4 font-semibold text-brand-fg shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60';
const fieldClass =
  'h-12 w-full rounded-lg border border-border bg-surface px-4 text-base text-fg shadow-sm transition placeholder:text-fg-subtle focus:border-brand focus:ring-2 focus:ring-brand/30';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlToken = searchParams.get('token');
  const [view, setView] = useState<VerifyView>('loading');
  const [rateLimited, setRateLimited] = useState(false);

  const resolve = useCallback(async (isCancelled: () => boolean) => {
    if (pendingCapture) await pendingCapture;
    const record = load();
    if (!record || (!record.token && !record.outcome)) {
      if (record) save(null);
      if (!isCancelled()) setView('missing');
      return;
    }
    if (record.outcome) {
      if (Date.now() - record.at > OUTCOME_TTL_MS) {
        save(null);
        if (!isCancelled()) setView('missing');
      } else if (!isCancelled()) {
        setView(record.outcome);
      }
      return;
    }
    if (!isCancelled()) setView('loading');
    const result = await submit(record.token!);
    if (isCancelled()) return;
    setRateLimited(!!result.rateLimited);
    setView(result.view);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (urlToken !== null) {
      // Capture, then drop the token from the visible URL and this history entry.
      pendingCapture = capture(urlToken.trim()).finally(() => { pendingCapture = null; });
      navigate('/verify', { replace: true });
      return;
    }
    void resolve(() => cancelled);
    return () => { cancelled = true; };
  }, [urlToken, navigate, resolve]);

  const retry = () => { setView('loading'); void resolve(() => false); };

  switch (view) {
    case 'loading':
      return (
        <AuthLayout title="Verifying your email" subtitle="This only takes a moment.">
          <p role="status" aria-live="polite" className="text-center text-sm text-fg-muted">Checking your verification link…</p>
        </AuthLayout>
      );
    case 'verified':
      return (
        <AuthLayout title="Email verified" subtitle="Your email address is confirmed.">
          <p role="status" className="mb-6 text-sm text-fg-muted">You can now sign in to Nifty Hosting.</p>
          <Link to="/login" replace className={linkButtonClass}>Continue to sign in</Link>
        </AuthLayout>
      );
    case 'used':
      return (
        <AuthLayout title="This link has already been used" subtitle="Verification links work only once.">
          <p role="alert" className="mb-6 text-sm text-fg-muted">
            If you verified a moment ago, your email is confirmed and you can sign in. If sign-in still asks you to
            verify, the link may have expired — request a new one below.
          </p>
          <Link to="/login" replace className={linkButtonClass}>Go to sign in</Link>
          <ResendForm />
        </AuthLayout>
      );
    case 'invalid':
      return (
        <AuthLayout title="This link is invalid or has expired" subtitle="Verification links expire after 24 hours, work only once, and only the newest link is valid.">
          <p role="alert" className="mb-2 text-sm text-fg-muted">Request a new verification email below, or sign in if you have already verified.</p>
          <ResendForm />
          <Link to="/login" replace className="mt-4 block text-center text-sm text-brand hover:underline">Back to sign in</Link>
        </AuthLayout>
      );
    case 'temporary':
      return (
        <AuthLayout title="We couldn't verify your email just now" subtitle="This is a temporary problem; your link has not been used up by it.">
          <p role="alert" className="mb-6 text-sm text-fg-muted">
            {rateLimited ? 'Too many attempts. Please wait a minute, then try again.' : 'Check your connection, then try again.'}
          </p>
          <button type="button" onClick={retry} className={buttonClass}>Try again</button>
        </AuthLayout>
      );
    case 'missing':
    default:
      return (
        <AuthLayout title="Verify your email" subtitle="Open the verification link from your email to confirm your address.">
          <p className="mb-2 text-sm text-fg-muted">No verification link was found on this page. If your link expired or you can't find the email, request a new one.</p>
          <ResendForm />
          <Link to="/login" replace className="mt-4 block text-center text-sm text-brand hover:underline">Back to sign in</Link>
        </AuthLayout>
      );
  }
}

const resendSchema = z.object({ email: z.string().email('Enter a valid email address') });

/** Always the same neutral confirmation: the API answers 202 whether or not the account exists. */
function ResendForm() {
  const [sent, setSent] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const form = useForm<z.infer<typeof resendSchema>>({ resolver: zodResolver(resendSchema), defaultValues: { email: '' } });

  const onSubmit = form.handleSubmit(async (values) => {
    setFailure(null);
    try {
      await apiClient.post('/users/verify/resend', values);
      setSent(true);
    } catch (err) {
      const status = isApiError(err) ? err.status : 0;
      setFailure(status === 429 ? 'Too many requests. Please wait a few minutes and try again.' : 'We could not send a new link right now. Please try again shortly.');
    }
  });

  if (sent) {
    return (
      <p role="status" className="mt-6 rounded-lg bg-surface-raised px-4 py-3 text-sm text-fg">
        If that address has an account waiting for verification, a new link is on its way. It expires in 24 hours.
      </p>
    );
  }
  return (
    <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate aria-label="Request a new verification email">
      <div>
        <label htmlFor="resend-email" className="mb-2 block text-sm font-medium text-fg">Email</label>
        <input id="resend-email" {...form.register('email')} type="email" autoComplete="email" className={fieldClass} />
        {form.formState.errors.email && <p className="mt-2 text-sm text-danger">{form.formState.errors.email.message}</p>}
      </div>
      {failure && <p role="alert" className="text-sm text-danger">{failure}</p>}
      <button type="submit" disabled={form.formState.isSubmitting} className={buttonClass}>
        {form.formState.isSubmitting ? 'Sending…' : 'Send a new link'}
      </button>
    </form>
  );
}

/** Test hook: reset module state between tests. */
export function __resetVerifyStateForTests(): void {
  memoryRecord = null;
  pendingCapture = null;
  inFlight.clear();
}
